/**
 * One leave type's balance for the employee — Total / Taken / Booked /
 * Pending / Available, the same five figures (and the same column labels) as
 * the employer-side Leave → Balances tab, so both sides always read
 * identically. Taken is approved leave on or before today; Booked is approved
 * leave still in the future (bug-bash 2026-10-15 #6).
 *
 * The single owner of the ESS balance rendering: the My Leave balance cards
 * and the Request Leave form's "selected type" panel both render this, the
 * form passing `compact` because it already sits inside a glass card.
 *
 * Layout: leave type + "N days available" headline, a meter of Total split
 * into taken (brand) / booked (`--accent-light`, a lighter brand tint — the
 * same leave, just not reached yet; deliberately not a semantic status
 * colour) / pending (`--color-dot-warning`, the exact token of the "In
 * Review" pill dot in the history table — "not settled yet") / still
 * available (the empty track), and a figures row whose dots double as the
 * meter's legend.
 */
import { useTranslation } from '@mukil/i18n';
import { cn } from '@jumpstone/ui-react';
import { useIsEssSurface } from '../../components/layout/ess_surface.js';
import {
  format_days_for_display,
  format_days_scaled,
  leave_balance_meter,
  type MyLeaveBalance,
} from './my_leave_utils.js';

interface MyLeaveBalanceSummaryProps {
  balance: MyLeaveBalance;
  /** Inline panel inside another surface (no glass, no headline). */
  compact?: boolean;
}

type Swatch = 'taken' | 'booked' | 'pending' | 'available' | null;

const SWATCH_CLASS: Record<Exclude<Swatch, null>, string> = {
  taken: 'bg-[color:var(--accent)]',
  booked: 'bg-[color:var(--accent-light)]',
  pending: 'bg-[color:var(--color-dot-warning)]',
  available: 'border border-[color:var(--ink-muted)]',
};

export function MyLeaveBalanceSummary({
  balance,
  compact = false,
}: MyLeaveBalanceSummaryProps): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const ess = useIsEssSurface();
  const type_label = t(`my_leave.leave_type.${balance.leave_type}`, {
    defaultValue: balance.leave_type,
  });
  const meter = leave_balance_meter(balance);
  const figures: Array<{
    key: string;
    label: string;
    value: number;
    swatch: Swatch;
  }> = [
    {
      key: 'total',
      label: t('leave.balances_column_total'),
      value: balance.total_days_scaled,
      swatch: null,
    },
    {
      key: 'taken',
      label: t('leave.balances_column_taken'),
      value: balance.taken_days_scaled,
      swatch: 'taken',
    },
    {
      key: 'booked',
      label: t('leave.balances_column_booked'),
      value: balance.booked_days_scaled,
      swatch: 'booked',
    },
    {
      key: 'pending',
      label: t('leave.balances_column_pending'),
      value: balance.pending_days_scaled,
      swatch: 'pending',
    },
    {
      key: 'available',
      label: t('leave.balances_column_available'),
      value: balance.available_days_scaled,
      swatch: 'available',
    },
  ];

  // Phone layout (employee surface, balance cards only): type and headline
  // share a row, a slimmer meter, and the same five figures inline. The
  // compact panel inside the request form and the employer console keep
  // the layout below.
  if (ess && !compact) {
    const headline_negative = balance.available_days_scaled < 0;
    return (
      <div
        className="glass rounded-[18px] px-4 py-3.5"
        data-testid={`my-leave-balance-${balance.leave_type}`}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <p className="text-[15px] font-bold text-[color:var(--ink)]">
            {type_label}
          </p>
          <p
            className={cn(
              'num text-[17px] font-bold leading-tight tracking-[-0.01em]',
              headline_negative ? 'curr-neg' : 'text-[color:var(--accent)]',
            )}
          >
            {t('my_leave.days_available', {
              count: format_days_scaled(balance.available_days_scaled),
            })}
          </p>
        </div>

        {/* Meter -- decorative; every figure it shows is in the row below. */}
        <div
          className="wp-ess-meter my-2.5 flex h-1.5 w-full overflow-hidden rounded-full"
          aria-hidden="true"
          data-testid="my-leave-balance-meter"
        >
          <span
            className="h-full bg-[color:var(--accent)]"
            style={{ width: `${String(meter.taken_pct)}%` }}
          />
          <span
            className="h-full bg-[color:var(--accent-light)]"
            style={{ width: `${String(meter.booked_pct)}%` }}
          />
          <span
            className="h-full bg-[color:var(--color-dot-warning)]"
            style={{ width: `${String(meter.pending_pct)}%` }}
          />
        </div>

        <dl className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
          {figures.map((f) => (
            <div
              key={f.key}
              data-testid={`my-leave-balance-${f.key}`}
              className="flex items-baseline gap-1"
            >
              <dt className="flex items-center gap-1.5 text-[color:var(--ink-muted)]">
                {f.swatch !== null && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'inline-block h-2 w-2 rounded-full',
                      SWATCH_CLASS[f.swatch],
                    )}
                  />
                )}
                {f.label}
              </dt>
              <dd
                className={cn(
                  'num font-bold text-[color:var(--ink)]',
                  f.key === 'available' && f.value < 0 && 'curr-neg',
                )}
              >
                {format_days_for_display(f.value, locale)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-2xl',
        compact ? 'border border-[color:var(--line)] p-4' : 'glass-soft p-5',
      )}
      data-testid={`my-leave-balance-${balance.leave_type}`}
    >
      <p className="text-[12px] uppercase tracking-[0.08em] text-[color:var(--ink-muted)] font-semibold">
        {type_label}
      </p>
      {!compact && (
        <p className="mt-1 text-[26px] leading-tight font-bold num text-[color:var(--ink)]">
          {t('my_leave.days_available', {
            count: format_days_scaled(balance.available_days_scaled),
          })}
        </p>
      )}

      {/* Meter — decorative; every figure it shows is in the row below. */}
      <div
        className={cn(
          'flex h-2 w-full overflow-hidden rounded-full bg-[color:var(--line)]',
          compact ? 'mt-2' : 'mt-4',
        )}
        aria-hidden="true"
        data-testid="my-leave-balance-meter"
      >
        <span
          className="h-full bg-[color:var(--accent)]"
          style={{ width: `${String(meter.taken_pct)}%` }}
        />
        <span
          className="h-full bg-[color:var(--accent-light)]"
          style={{ width: `${String(meter.booked_pct)}%` }}
        />
        <span
          className="h-full bg-[color:var(--color-dot-warning)]"
          style={{ width: `${String(meter.pending_pct)}%` }}
        />
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-5">
        {figures.map((f) => (
          <div
            key={f.key}
            data-testid={`my-leave-balance-${f.key}`}
            className={cn(
              f.key === 'total' &&
                'sm:border-r sm:border-[color:var(--line)] sm:pr-4',
            )}
          >
            <dt className="flex items-center gap-1.5 text-[12px] text-[color:var(--ink-muted)]">
              {f.swatch !== null && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'inline-block h-2 w-2 rounded-full',
                    SWATCH_CLASS[f.swatch],
                  )}
                />
              )}
              {f.label}
            </dt>
            <dd
              className={cn(
                'num font-semibold text-[color:var(--ink)]',
                compact ? 'text-[15px]' : 'text-[18px]',
                f.key === 'available' && f.value < 0 && 'curr-neg',
              )}
            >
              {format_days_for_display(f.value, locale)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
